import { Plus } from "@phosphor-icons/react";
import { useEffect, useState } from "react";

import PoweredByStripe from "common/components/PoweredByStripe";
import { Button } from "common/components/ui/Button";
import { DialogDescription, DialogTitle } from "common/components/ui/Dialog";
import { DrawerDescription, DrawerTitle } from "common/components/ui/Drawer";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "common/components/ui/Tooltip";
import usePaymentSourceMutations from "common/datahooks/usePaymentSourceMutations";
import { useSelectedWorkspaceContext } from "common/helpers/SelectedWorkspaceContext";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { PaymentSource } from "common/types";

import PaymentSourceOption from "./PaymentSourceOption";

interface PaymentMethodSelectionProps {
  subscriptionId: string;
  paymentSources: PaymentSource[];
  selectedPaymentSource: PaymentSource;
  addPaymentMethod: () => void;
  onBack: () => void;
  onSelect?: (paymentSource: PaymentSource) => void;
}

export default function PaymentMethodSelection({
  subscriptionId,
  paymentSources,
  selectedPaymentSource,
  addPaymentMethod,
  onBack,
  onSelect = undefined,
}: PaymentMethodSelectionProps) {
  const isDesktop = useTwBreakpoint("lg");
  const selectedWorkspaceContext = useSelectedWorkspaceContext();

  // Local state for tracking the currently selected payment method
  const [selectedMethod, setSelectedMethod] = useState(selectedPaymentSource);

  const { setPaymentSource, isSettingPaymentSource } =
    usePaymentSourceMutations();

  useEffect(() => {
    setSelectedMethod(selectedPaymentSource);
  }, [selectedPaymentSource]);

  function handleSubmit() {
    if (onSelect) {
      onSelect(selectedMethod);
    } else {
      setPaymentSource({ paymentSource: selectedMethod, subscriptionId });
    }

    onBack();
  }

  const cantAddPaymentMethod =
    selectedWorkspaceContext && selectedWorkspaceContext.role !== "owner";

  const addPaymentMethodButton = (
    <button
      type="button"
      className="group flex items-center justify-start gap-2 rounded-3xl border border-dashed border-black-300 bg-white px-2 py-1.5 transition-colors duration-300 ease-in-out hover:bg-black-100 disabled:cursor-not-allowed disabled:bg-black-100"
      disabled={cantAddPaymentMethod}
      onClick={addPaymentMethod}
    >
      <div className="size-8 rounded-full bg-black-900 flex group-disabled:bg-black-200">
        <Plus
          color="white"
          size={16}
          className="group-disabled:fill-black-950 m-auto"
        />
      </div>

      <span className="text-sm-bold group-disabled:text-black-400">
        Add payment method
      </span>
    </button>
  );

  const Title = isDesktop ? DialogTitle : DrawerTitle;
  const Description = isDesktop ? DialogDescription : DrawerDescription;

  return (
    <>
      <Title>Select your preferred payment method</Title>

      <Description className="mb-8">
        Set a preferred payment method for your subscription. You can change
        this at any time
      </Description>

      <div className="flex flex-col gap-3">
        <div className="flex max-h-[272px] flex-col gap-y-3 overflow-y-auto p-1 scrollbar-thin">
          {/* Payment methods */}
          {paymentSources.map((paymentSource) => (
            <PaymentSourceOption
              key={paymentSource.id}
              paymentSource={paymentSource}
              selectedMethod={selectedMethod}
              setSelectedMethod={setSelectedMethod}
            />
          ))}
        </div>

        {cantAddPaymentMethod ? (
          <Tooltip>
            <TooltipTrigger asChild>{addPaymentMethodButton}</TooltipTrigger>
            <TooltipContent>
              Only the Owner can add a new payment method
            </TooltipContent>
          </Tooltip>
        ) : (
          addPaymentMethodButton
        )}
      </div>
      <PoweredByStripe />

      <div className="flex flex-row items-center justify-between mt-8">
        <Button variant="secondary-black" onClick={onBack}>
          Back
        </Button>

        <Button onClick={handleSubmit} isLoading={isSettingPaymentSource}>
          Confirm
        </Button>
      </div>
    </>
  );
}
